import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BellIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  XMarkIcon,
  WalletIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { User } from '../../types';
import { formatCurrency } from '../../utils/currency';
import BackButton from '../common/BackButton';

interface HeaderProps {
  onMenuClick?: () => void;
  user: User | null;
}

interface Notification {
  id: string;
  _id?: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'announcement';
  title: string;
  message: string;
  timestamp: Date;
  createdAt?: string;
  read: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, user }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { connected, socket } = useSocket();
  const { isAuthenticated, logout } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [, setShowConnectionStatus] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const connectionRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const hasFetched = useRef(false);
  const isFetchingNotifications = useRef(false);
  
  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // Loyalty points chip
  const [loyaltyPoints, setLoyaltyPoints] = useState<number | null>(null);

  // Map backend notification types to UI types
  const mapNotificationType = (type: string): Notification['type'] => {
    if (type.includes('announcement')) return 'announcement';
    if (type.includes('success') || type.includes('completed')) return 'success';
    if (type.includes('warning')) return 'warning';
    if (type.includes('error') || type.includes('failed')) return 'error';
    return 'info';
  };

  // Resolve a valid notification date; fall back to the ObjectId's embedded
  // creation timestamp when createdAt is missing or unparseable (stale cached
  // responses / legacy documents), never surfacing "Invalid Date".
  const resolveNotificationDate = (n: any): Date => {
    const parsed = n?.createdAt ? new Date(n.createdAt) : null;
    if (parsed && !Number.isNaN(parsed.getTime())) return parsed;
    const hex = String(n?._id || '').slice(0, 8);
    if (/^[0-9a-f]{8}$/i.test(hex)) {
      const epoch = parseInt(hex, 16) * 1000;
      if (Number.isFinite(epoch)) return new Date(epoch);
    }
    return new Date();
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || isFetchingNotifications.current) return;
    
    try {
      isFetchingNotifications.current = true;
      setLoading(true);
      const response = await api.get('/notifications?limit=20');
      
      if (response.data.success) {
        const apiNotifications = response.data.data.notifications.map((n: any) => ({
          id: n._id,
          _id: n._id,
          type: mapNotificationType(n.type),
          title: n.title,
          message: n.message,
          timestamp: resolveNotificationDate(n),
          read: n.read,
        }));
        
        setNotifications(apiNotifications);
        setUnreadCount(response.data.data.unreadCount);
      }
    } catch (error) {

    } finally {
      setLoading(false);
      isFetchingNotifications.current = false;
    }
  }, [isAuthenticated]);

  // Fetch on mount
  useEffect(() => {
    if (isAuthenticated && !hasFetched.current) {
      hasFetched.current = true;
      fetchNotifications();
    }
  }, [isAuthenticated, fetchNotifications]);

  // PWA Install prompt handler
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Handle PWA install
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    setDeferredPrompt(null);
  };

  // Loyalty points — fetch on mount and refresh on order events
  useEffect(() => {
    if (!user || user.role !== 'user') return;
    let cancelled = false;
    const refresh = () => {
      api.get('/loyalty-points/summary')
        .then(res => { if (!cancelled) setLoyaltyPoints(res.data?.data?.availablePoints ?? null); })
        .catch(() => {});
    };
    refresh();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!socket || !user || user.role !== 'user') return;
    const refresh = () => {
      api.get('/loyalty-points/summary')
        .then(res => setLoyaltyPoints(res.data?.data?.availablePoints ?? null))
        .catch(() => {});
    };
    socket.on('order:completed', refresh);
    socket.on('order:status_update', refresh);
    return () => {
      socket.off('order:completed', refresh);
      socket.off('order:status_update', refresh);
    };
  }, [socket, user]);

  // Refetch when dropdown opens
  useEffect(() => {
    if (showNotifications && isAuthenticated) {
      fetchNotifications();
    }
  }, [showNotifications, isAuthenticated, fetchNotifications]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (connectionRef.current && !connectionRef.current.contains(event.target as Node)) {
        setShowConnectionStatus(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    try {
      await api.put(`/notifications/${id}/read`);
    } catch (error) {

    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    try {
      await api.put('/notifications/read-all');
    } catch (error) {

    }
  };

  // Clear all notifications
  const clearAll = async () => {
    setNotifications([]);
    setUnreadCount(0);
    
    try {
      await api.delete('/notifications/clear-all');
    } catch (error) {

    }
  };

  // Get notification icon
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'announcement':
        return <MegaphoneIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  // Get notification background

  // Format time ago
  const formatTimeAgo = (date: Date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Get greeting based on time
  // Get role badge color
  return (
    <header className="bg-white dark:bg-navy-900 border-b border-gray-100 dark:border-navy-800">
      <div className="px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left: Menu + Back Button + Wallet Balance */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Mobile menu */}
            {onMenuClick && (
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 -ml-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              </button>
            )}

            {/* Global Back Button */}
            <BackButton className="hidden sm:inline-flex" />

            {/* Loyalty Points Chip */}
            {loyaltyPoints !== null && loyaltyPoints > 0 && user?.role === 'user' && (
              <button
                onClick={() => navigate('/loyalty-points')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-400/10 border border-amber-200 dark:border-amber-400/20 hover:bg-amber-100 dark:hover:bg-amber-400/20 transition-colors"
                title="Loyalty Points — click to redeem"
              >
                <span className="text-base leading-none">⭐</span>
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                  {loyaltyPoints.toLocaleString()} pts
                </span>
              </button>
            )}

            {/* Wallet Balance Card */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/wallet?topup=1')}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl cursor-pointer shadow-lg shadow-primary-500/20 min-w-0 max-w-[55vw] sm:max-w-none"
            >
              <WalletIcon className="h-5 w-5 text-white" />
              <div className="hidden sm:block">
                <p className="text-[10px] text-white/70 font-medium leading-none">Balance</p>
                <p className="text-sm font-bold text-white leading-tight">
                  {formatCurrency(user?.wallet?.balance || 0)}
                </p>
              </div>
              <span className="sm:hidden text-sm font-bold text-white truncate min-w-0">
                {formatCurrency(user?.wallet?.balance || 0)}
              </span>
              <div className="hidden sm:flex items-center justify-center w-6 h-6 bg-white/20 rounded-lg">
                <PlusIcon className="h-4 w-4 text-white" />
              </div>
            </motion.div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* PWA Install Button */}
            {showInstallButton && (
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleInstallClick}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl shadow-lg shadow-green-500/20 hover:shadow-xl transition-all"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span className="hidden sm:inline text-sm font-semibold">Install</span>
              </motion.button>
            )}

            {/* Theme toggle */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-gray-100 dark:bg-navy-800 hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors"
            >
              {theme === 'dark' ? (
                <SunIcon className="h-5 w-5 text-amber-500" />
              ) : (
                <MoonIcon className="h-5 w-5 text-gray-600" />
              )}
            </motion.button>

            {/* Notifications */}
            <div className="relative" ref={dropdownRef}>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowNotifications(!showNotifications)}
                className={clsx(
                  'relative p-2.5 rounded-xl transition-colors',
                  showNotifications
                    ? 'bg-primary-100 dark:bg-primary-900/30'
                    : 'bg-gray-100 dark:bg-navy-800 hover:bg-gray-200 dark:hover:bg-navy-700'
                )}
              >
                {unreadCount > 0 ? (
                  <BellAlertIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                ) : (
                  <BellIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                )}
                
                <AnimatePresence>
                  {unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>

              {/* Notifications dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-16 sm:top-full mt-0 sm:mt-2 w-auto sm:w-96 max-w-sm sm:max-w-none bg-white dark:bg-navy-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-navy-700 overflow-hidden z-[100]"
                  >
                    <div className="px-4 py-3 bg-gray-50 dark:bg-navy-900/60 border-b border-gray-200 dark:border-navy-700">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white min-w-0 truncate">Notifications</h3>
                        <div className="flex flex-wrap items-center gap-2">
                          {unreadCount > 0 && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full">
                              {unreadCount} new
                            </span>
                          )}
                          <button onClick={() => setShowNotifications(false)}>
                            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {notifications.length > 0 && (
                      <div className="px-4 py-2 border-b border-gray-100 dark:border-navy-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <button onClick={markAllAsRead} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                          Mark all read
                        </button>
                        <button onClick={clearAll} className="text-xs text-gray-500 hover:text-red-500 font-medium">
                          Clear all
                        </button>
                      </div>
                    )}
                    
                    <div className="max-h-[calc(100vh-7.5rem)] sm:max-h-80 overflow-y-auto">
                      {loading ? (
                        <div className="p-8 text-center">
                          <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <BellIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No notifications</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => !notification.read && markAsRead(notification.id)}
                              className={clsx(
                                'px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700/50',
                                !notification.read && 'bg-primary-50/50 dark:bg-primary-900/10'
                              )}
                            >
                              <div className="flex gap-3">
                                <div className="flex-shrink-0">{getNotificationIcon(notification.type)}</div>
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={clsx(
                                      'text-sm min-w-0 overflow-hidden text-ellipsis [overflow-wrap:anywhere] line-clamp-1',
                                      !notification.read
                                        ? 'font-medium text-gray-900 dark:text-white'
                                        : 'text-gray-600 dark:text-gray-400'
                                    )}
                                  >
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 [overflow-wrap:anywhere]">{notification.message}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{formatTimeAgo(notification.timestamp)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Avatar with Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <motion.button 
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 cursor-pointer focus:outline-none"
              >
                <div className="hidden sm:block text-right max-w-[140px]">
                  <p className="text-sm font-medium text-gray-900 dark:text-white leading-tight">
                    {user?.profile?.firstName || user?.username}
                  </p>
                  <p className={clsx(
                    'text-xs capitalize',
                    user?.role === 'admin' ? 'text-red-500' : user?.role === 'agent' ? 'text-purple-500' : 'text-gray-500'
                  )}>
                    {user?.role || 'user'}
                  </p>
                </div>
                <div className="relative">
                  <div className="h-10 w-10 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-500 dark:to-primary-700 rounded-full flex items-center justify-center ring-2 ring-primary-200 dark:ring-primary-800 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                    <span className="text-white text-base font-bold">
                      {(user?.profile?.firstName || user?.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className={clsx(
                    'absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-navy-900 shadow-sm',
                    connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                  )}></span>
                </div>
                <ChevronDownIcon className={clsx(
                  'h-4 w-4 text-gray-500 transition-transform duration-200 hidden sm:block',
                  showUserMenu && 'rotate-180'
                )} />
              </motion.button>

              {/* User Dropdown Menu */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-56 bg-white dark:bg-navy-800 rounded-xl shadow-xl border border-gray-200 dark:border-navy-700 py-2 z-[100]"
                  >
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-navy-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user?.profile?.firstName} {user?.profile?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user?.email}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profile');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
                      >
                        <UserIcon className="h-4 w-4" />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/settings');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        Settings
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/wallet');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
                      >
                        <WalletIcon className="h-4 w-4" />
                        Wallet
                      </button>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-200 dark:border-navy-700 pt-1">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
